/**
 * use-completed-jobs — Completed Jobs tab data (Quick 260616-cmpl).
 *
 * GET /v1/jobs/completed (VIEW_RUN_TICKETS) — server-side search / filter / sort /
 * pagination. GET /v1/jobs/:id/audit (VIEW_RUN_TICKETS) — enriched job_events +
 * timeclock_events (+ driver ticket reprints) timeline for the basic/detailed audit view.
 */

import { useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';

/** One row in the completed-jobs grid. */
export interface CompletedJobRow {
  readonly id: string;
  readonly haulId: string | null;
  readonly operatorName: string | null;
  readonly siteName: string | null;
  readonly driverName: string | null;
  readonly jobTypeLabel: string | null;
  readonly statusCode: string;
  readonly statusLabel: string;
  readonly totalBbl: number | null;
  readonly capturedAt: string | null;
  readonly completedAt: string | null;
}

export interface CompletedJobsResponse {
  readonly jobs: readonly CompletedJobRow[];
  readonly total: number;
}

/** Sortable column keys — must match the server allowlist. */
export type CompletedSortKey =
  | 'haulId'
  | 'operatorName'
  | 'siteName'
  | 'driverName'
  | 'jobTypeLabel'
  | 'totalBbl'
  | 'capturedAt'
  | 'completedAt'
  | 'statusCode';

/** Statuses the completed-jobs endpoint accepts in its status filter. */
export const COMPLETED_STATUS_OPTIONS = [
  'TO_BE_INVOICED',
  'IN_REVIEW',
  'COMPLETED',
  'INVOICED',
  'CANCELLED',
  'REJECTED',
] as const;
export type CompletedStatus = (typeof COMPLETED_STATUS_OPTIONS)[number];

/**
 * Completed Jobs tab status filter (field-test fix). Billing managers triage
 * post-work tickets by review/billing state, so the tab offers a single-select
 * filter: All / To Be Invoiced / In Review / Completed. "All" (the default)
 * sends the union of the three post-work states so everything awaiting review
 * or invoicing shows in one view.
 */
export interface CompletedFilter {
  readonly key: 'ALL' | 'TO_BE_INVOICED' | 'IN_REVIEW' | 'COMPLETED';
  readonly label: string;
  readonly statuses: readonly CompletedStatus[];
}

export const COMPLETED_FILTERS: readonly CompletedFilter[] = [
  // Field-test fix #3 (LOD000000008) — "All" now spans EVERY post-work status
  // (COMPLETED, TO_BE_INVOICED, IN_REVIEW, INVOICED, CANCELLED) so a ticket that
  // auto-advanced past COMPLETED (Task 11.1 → TO_BE_INVOICED/IN_REVIEW), or was
  // invoiced/cancelled, is never missing from the default view.
  {
    key: 'ALL',
    label: 'All',
    statuses: ['COMPLETED', 'TO_BE_INVOICED', 'IN_REVIEW', 'INVOICED', 'CANCELLED'],
  },
  { key: 'TO_BE_INVOICED', label: 'To Be Invoiced', statuses: ['TO_BE_INVOICED'] },
  { key: 'IN_REVIEW', label: 'In Review', statuses: ['IN_REVIEW'] },
  { key: 'COMPLETED', label: 'Completed', statuses: ['COMPLETED'] },
];

export interface CompletedJobsParams {
  readonly q: string;
  readonly status: readonly string[];
  readonly operatorId: string;
  readonly driverId: string;
  readonly jobTypeCode: string;
  /** Local date 'YYYY-MM-DD' (inclusive start). */
  readonly dateFrom: string;
  /** Local date 'YYYY-MM-DD' (inclusive end). */
  readonly dateTo: string;
  /**
   * IANA timezone (DEC-004) the dateFrom/dateTo day boundaries are expressed in,
   * e.g. 'America/Chicago'. The server converts the local day to a UTC range via
   * `AT TIME ZONE` so the filter buckets by the dispatcher's local day, not UTC.
   */
  readonly tz: string;
  readonly sort: CompletedSortKey;
  readonly dir: 'asc' | 'desc';
  readonly limit: number;
  readonly offset: number;
}

/**
 * Build the GET /v1/jobs/completed query string. Date-only inputs are widened to
 * cover the full local day (start 00:00:00 / end 23:59:59) so the end day is
 * inclusive, and the dispatcher's IANA `tz` is sent so the server interprets
 * those boundaries in local time, not UTC (DEC-004).
 */
export function buildCompletedQuery(p: CompletedJobsParams): string {
  const sp = new URLSearchParams();
  if (p.q.trim() !== '') sp.set('q', p.q.trim());
  if (p.status.length > 0) sp.set('status', p.status.join(','));
  if (p.operatorId !== '') sp.set('operatorId', p.operatorId);
  if (p.driverId !== '') sp.set('driverId', p.driverId);
  if (p.jobTypeCode !== '') sp.set('jobTypeCode', p.jobTypeCode);
  if (p.dateFrom !== '') sp.set('dateFrom', `${p.dateFrom}T00:00:00`);
  if (p.dateTo !== '') sp.set('dateTo', `${p.dateTo}T23:59:59`);
  // Only meaningful alongside a date filter, but always sent — harmless otherwise.
  if (p.tz !== '') sp.set('tz', p.tz);
  sp.set('sort', p.sort);
  sp.set('dir', p.dir);
  sp.set('limit', p.limit.toString());
  sp.set('offset', p.offset.toString());
  return sp.toString();
}

/** Path for GET /v1/jobs/completed with the given params. */
export function completedJobsPath(p: CompletedJobsParams): string {
  return `/v1/jobs/completed?${buildCompletedQuery(p)}`;
}

export function useCompletedJobs(
  apiOpts: ReturnType<typeof useApiOpts>,
  params: CompletedJobsParams,
): ReturnType<typeof useApiQuery<CompletedJobsResponse>> {
  return useApiQuery<CompletedJobsResponse>(apiOpts, completedJobsPath(params), {
    queryKey: ['jobs', 'completed', params],
    staleTime: 10_000,
  });
}

// ── Audit timeline ───────────────────────────────────────────────────────────

export interface AuditEvent {
  readonly source: 'job_event' | 'timeclock' | 'audit';
  readonly eventType: string;
  readonly occurredAt: string;
  readonly syncedAt: string | null;
  readonly actorName: string | null;
  readonly triggeredBy: string | null;
  readonly online: boolean | null;
  readonly gpsLat: number | null;
  readonly gpsLng: number | null;
  readonly gpsAccuracyFt: number | null;
  readonly facilityName: string | null;
  // Phase 8 / Task 8.4 — GPS verification result on arrival events.
  readonly verificationResult: string | null;
  readonly distanceFromCenter: number | null;
  readonly verificationThresholdFt: number | null;
  // OFFICIAL_TICKET_REPRINTED only — which ticket was reprinted (PICKUP / DISPOSAL / PROVISIONAL).
  readonly ticketType?: string | null;
  // HAUL_ID_ASSIGNED only — the offline P-id and the HaulId that replaced it (DEC-038).
  readonly provisionalHaulId?: string | null;
  readonly canonicalHaulId?: string | null;
  // TIME_CHECK only (DEC-352) — DEVICE_CLOCK / ARRIVED_LATE / CLOCK_UNKNOWN /
  // PAUSED_BY_FLAGGED_RECORD (OPEN-160).
  readonly timeCheckCase?: string | null;
  // BILLING_TICKET_EDITED (#50, DEC-356) and the #60 site changes — the reason typed.
  readonly editReason?: string | null;
  // #60 (DEC-365) JOB_SITE_CHANGED only — the site names before and after ("SITE · WELL").
  readonly siteFrom?: string | null;
  readonly siteTo?: string | null;
  // #60 (DEC-365) site changes — the reason code (JOB_SITE_CHANGE_REASON_CODES).
  readonly siteChangeReasonCode?: string | null;
  // OPEN-157 — JOB_TAKEN_BACK / JOB_DRIVER_CORRECTED only: the drivers, named. driverTo null on
  // a take-back means Unassigned. The reason rides editReason.
  readonly driverFrom?: string | null;
  readonly driverTo?: string | null;
}

export interface JobAudit {
  readonly jobCreatedAt: string | null;
  readonly jobCreatedByName: string | null;
  readonly completedAt: string | null;
  readonly totalBbl: number | null;
  readonly events: readonly AuditEvent[];
}

export const JOB_AUDIT_QUERY_KEY = (jobId: string): readonly [string, string] =>
  ['job-audit', jobId] as const;

/** Fetch a job's enriched audit timeline. Pass `null` to disable (detail closed). */
export function useJobAudit(
  apiOpts: ReturnType<typeof useApiOpts>,
  jobId: string | null,
): ReturnType<typeof useApiQuery<JobAudit>> {
  return useApiQuery<JobAudit>(apiOpts, `/v1/jobs/${jobId ?? ''}/audit`, {
    queryKey: JOB_AUDIT_QUERY_KEY(jobId ?? '__none__'),
    enabled: jobId !== null,
    staleTime: 10_000,
  });
}

// ── GPS breadcrumb trail (Phase 8 / Task 8.5) ────────────────────────────────

export interface GpsTrailPoint {
  readonly lat: number | null;
  readonly lng: number | null;
  readonly accuracyFt: number | null;
  readonly speedMph: number | null;
  readonly headingDeg: number | null;
  readonly capturedAt: string;
  readonly eventType: string;
  readonly eventLabel: string | null;
}

export interface GpsTrailGeofenceEvent {
  readonly eventType: string;
  readonly triggeredBy: string;
  readonly gpsLat: number | null;
  readonly gpsLng: number | null;
  readonly gpsAccuracyFt: number | null;
  readonly distanceFromCenter: number | null;
  readonly dwellSeconds: number | null;
  readonly verificationResult: string | null;
  readonly verificationThresholdFt: number | null;
  // OFFICIAL_TICKET_REPRINTED only — which ticket was reprinted (PICKUP / DISPOSAL / PROVISIONAL).
  readonly ticketType?: string | null;
  readonly occurredAt: string;
}

export interface GpsTrail {
  readonly points: readonly GpsTrailPoint[];
  readonly geofenceEvents: readonly GpsTrailGeofenceEvent[];
}

/** Fetch a job's GPS breadcrumb trail + geofence events. Pass `null` to disable. */
export function useGpsTrail(
  apiOpts: ReturnType<typeof useApiOpts>,
  jobId: string | null,
): ReturnType<typeof useApiQuery<GpsTrail>> {
  return useApiQuery<GpsTrail>(apiOpts, `/v1/jobs/${jobId ?? ''}/gps-trail`, {
    queryKey: ['job-gps-trail', jobId ?? '__none__'],
    enabled: jobId !== null,
    staleTime: 10_000,
  });
}
